import React, { useMemo } from "react";
import { useWatch } from "react-hook-form";

import {
  Field,
  FieldHelper,
  FieldLabel,
  FormCard,
  LabelRow,
} from "../../../../../components/FormPanel";
import {
  FormInput,
  FormMoneyInput,
  FormToggle,
  InputDate,
  SelectForm,
  SelectOptionTypes,
} from "../../../../../components/Input";
import { NetPreview } from "../../../../../components/NetPreview";
import { NetToGrossHelper } from "../../../../../components/NetToGrossHelper";
import { getCurrencySymbol } from "../../../../../utils/numbers";
import { toIsoDate } from "../../../../../utils/dates";
import { amountLabelOf } from "../../../../../utils/taxWithheld";
import { CASHBACK_EARNED_RULES, CASHBACK_LOADING_MESSAGE } from "../constants";
import { DuplicateBanner } from "../DuplicateBanner";
import { TransactionFieldsProps, TransactionTypeOptions } from "../types";
import { TypeSegments } from "../TypeSegments";
import Button, { ButtonTypes } from "../../../../../components/Button";
import { DateAmountRow, ToggleRow } from "./styles";

const currencySymbol = getCurrencySymbol("Euro");

export const DesktopTransactionForm: React.FC<TransactionFieldsProps> = ({
  action,
  transaction,
  originalDate,
  control,
  register,
  setValue,
  isSubmitting,
  validationRules,
  typeOptions,
  categoryOptions,
  subCategoryOptions,
  accountOptions,
  loanToAccountOptions,
  frequencyOptions,
  shouldShowPrincipalAmount,
  shouldShowTax,
  shouldShowTaxSwitch,
  isTaxSwitchDisabled,
  taxSwitchHelperText,
  shouldShowCategory,
  shouldShowSubCategory,
  shouldShowFromAccount,
  shouldShowToAccount,
  isRecurringDisabled,
  recurringHelperText,
  isRecurring,
  maxTransactionDate,
  cashbackEarnedLabel,
  cashbackEarnedHelper,
  cashbackPrefillError,
  isCashbackPrefillPending,
}) => {
  const transactionType = useWatch({ control, name: "transactionType" });
  const typeValue = transactionType?.value;
  const isDuplicate = action === "duplicate";

  const handleTypeChange = (option: SelectOptionTypes) => {
    setValue("transactionType", option, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSetToday = () => {
    const today = toIsoDate(new Date());
    setValue("transactionDate", today, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const showSingleAccountLabel = shouldShowFromAccount !== shouldShowToAccount;
  const fromAccountLabel = showSingleAccountLabel ? "Account" : "From account";
  const toAccountLabel = showSingleAccountLabel ? "Account" : "To account";

  const toAccountOptions = useMemo(() => {
    if (typeValue === TransactionTypeOptions.LOAN_PAYMENT) {
      return loanToAccountOptions;
    }
    return accountOptions;
  }, [typeValue, loanToAccountOptions, accountOptions]);

  return (
    <>
      {isDuplicate ? <DuplicateBanner originalDate={originalDate} /> : null}

      <FormCard>
        <Field>
          <LabelRow>
            <FieldLabel>Type *</FieldLabel>
          </LabelRow>
          <TypeSegments
            options={typeOptions}
            selectedValue={typeValue}
            disabled={isSubmitting}
            onChange={handleTypeChange}
          />
        </Field>

        <DateAmountRow>
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="transaction-date">Date *</FieldLabel>
              {isDuplicate ? (
                <Button
                  type={ButtonTypes.link}
                  size="small"
                  onClick={handleSetToday}
                  disabled={isSubmitting}
                >
                  Today
                </Button>
              ) : null}
            </LabelRow>
            <InputDate
              testId="transaction-date"
              rules={validationRules.transactionDate}
              name="transactionDate"
              control={control}
              placeholder="YYYY/MM/DD"
              maxDate={maxTransactionDate}
              disabled={isSubmitting}
            />
          </Field>

          <Field>
            <LabelRow>
              <FieldLabel htmlFor="transaction-amount">{`${amountLabelOf(shouldShowTax)} (${currencySymbol}) *`}</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="transaction-amount"
              rules={validationRules.amount}
              name="amount"
              control={control}
              placeholder="Enter Amount"
              disabled={isSubmitting}
              autoFocus={true}
            />
          </Field>
        </DateAmountRow>

        {cashbackEarnedLabel && (
          <Field>
            <FieldLabel htmlFor="transaction-cashback-earned">
              {cashbackEarnedLabel}
            </FieldLabel>
            <FormMoneyInput
              id="transaction-cashback-earned"
              name="cashbackEarned"
              control={control}
              disabled={isSubmitting}
              rules={CASHBACK_EARNED_RULES}
            />
            <FieldHelper>{cashbackEarnedHelper}</FieldHelper>
          </Field>
        )}
        {isCashbackPrefillPending && (
          <FieldHelper role="status">{CASHBACK_LOADING_MESSAGE}</FieldHelper>
        )}
        {cashbackPrefillError && (
          <FieldHelper role="status">{cashbackPrefillError}</FieldHelper>
        )}
        {shouldShowTaxSwitch ? (
          <Field>
            <ToggleRow>
              <FieldLabel>Tax withheld</FieldLabel>
              <FormToggle
                label=""
                description=""
                name="hasTaxWithheld"
                control={control}
                disabled={isSubmitting || isTaxSwitchDisabled}
                testId="tax-withheld-toggle"
              />
            </ToggleRow>
            {taxSwitchHelperText ? (
              <FieldHelper>{taxSwitchHelperText}</FieldHelper>
            ) : null}
          </Field>
        ) : null}

        {shouldShowTax ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="transaction-tax">{`Tax withheld (${currencySymbol}) *`}</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="transaction-tax"
              rules={validationRules.taxWithheld}
              name="taxWithheld"
              control={control}
              placeholder="Enter Tax Withheld"
              disabled={isSubmitting}
            />
            <NetPreview control={control} />
            <NetToGrossHelper setValue={setValue} disabled={isSubmitting} />
          </Field>
        ) : null}

        {shouldShowPrincipalAmount ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="transaction-principal">{`Principal amount (${currencySymbol}) *`}</FieldLabel>
            </LabelRow>
            <FormMoneyInput
              id="transaction-principal"
              rules={validationRules.principalAmount}
              name="principalAmount"
              control={control}
              placeholder="Enter Principal Amount"
              disabled={isSubmitting}
            />
          </Field>
        ) : null}
      </FormCard>

      <FormCard>
        {shouldShowCategory ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="category-select">Category</FieldLabel>
            </LabelRow>
            <SelectForm
              testId="category-select"
              {...register("category")}
              required={false}
              name="category"
              defaultValue={transaction?.category ?? null}
              control={control}
              options={categoryOptions}
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        {shouldShowSubCategory ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="subcategory-select">Subcategory</FieldLabel>
            </LabelRow>
            <SelectForm
              testId="subcategory-select"
              name="subCategory"
              required={false}
              control={control}
              defaultValue={transaction?.subCategory ?? null}
              options={subCategoryOptions}
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        {shouldShowFromAccount ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="from-account-select">
                {`${fromAccountLabel} *`}
              </FieldLabel>
            </LabelRow>
            <SelectForm
              testId="from-account-select"
              key="fromAccount"
              rules={validationRules.fromAccountId}
              name="fromAccount"
              defaultValue={transaction?.fromAccount ?? null}
              control={control}
              options={accountOptions}
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        {shouldShowToAccount ? (
          <Field>
            <LabelRow>
              <FieldLabel htmlFor="to-account-select">
                {`${toAccountLabel} *`}
              </FieldLabel>
            </LabelRow>
            <SelectForm
              testId="to-account-select"
              key="toAccount"
              rules={validationRules.toAccountId}
              name="toAccount"
              defaultValue={transaction?.toAccount ?? null}
              control={control}
              options={toAccountOptions}
              disabled={isSubmitting}
            />
          </Field>
        ) : null}

        <Field>
          <LabelRow>
            <FieldLabel htmlFor="transaction-note">Note</FieldLabel>
          </LabelRow>
          <FormInput
            id="transaction-note"
            rules={validationRules.note}
            name="note"
            control={control}
            placeholder="Enter Description"
            handleClear={false}
            disabled={isSubmitting}
          />
        </Field>
      </FormCard>

      <FormCard>
        <Field>
          <ToggleRow>
            <FieldLabel>Recurring transaction</FieldLabel>
            <FormToggle
              label=""
              description=""
              {...register("isRecurring")}
              name="isRecurring"
              control={control}
              disabled={isSubmitting || isRecurringDisabled}
              testId="recurring-toggle"
            />
          </ToggleRow>
          {recurringHelperText ? (
            <FieldHelper>{recurringHelperText}</FieldHelper>
          ) : null}
        </Field>

        {isRecurring ? (
          <>
            <Field>
              <LabelRow>
                <FieldLabel htmlFor="frequency-select">Frequency *</FieldLabel>
              </LabelRow>
              <SelectForm
                testId="frequency-select"
                rules={validationRules.frequency}
                name="frequency"
                control={control}
                options={frequencyOptions}
                disabled={isSubmitting}
                defaultValue={transaction?.frequency ?? null}
              />
            </Field>

            <Field>
              <LabelRow>
                <FieldLabel htmlFor="transaction-interval">
                  Interval *
                </FieldLabel>
              </LabelRow>
              <FormInput
                id="transaction-interval"
                rules={validationRules.interval}
                name="interval"
                control={control}
                placeholder="Enter Interval"
                type="number"
                handleClear={false}
                disabled={isSubmitting}
              />
            </Field>

            <Field>
              <LabelRow>
                <FieldLabel htmlFor="transaction-stop-after">
                  Stop after occurrences
                </FieldLabel>
              </LabelRow>
              <FormInput
                id="transaction-stop-after"
                rules={validationRules.stopAfterOccurrences}
                name="stopAfterOccurrences"
                control={control}
                type="number"
                placeholder="Occurrences or empty for no limit"
                disabled={isSubmitting}
              />
            </Field>
          </>
        ) : null}
      </FormCard>
    </>
  );
};
